import { useEffect, useState } from 'react';
import { getHighlighter, shikiLang, shikiTheme, type Theme } from '../lib/shiki';

interface Props {
  code: string;
  language: string | null | undefined;
  theme: Theme;
  startLine?: number;
  /** When true, render in a smaller font with a tighter line-height (used for result snippets) */
  compact?: boolean;
}

/** Shiki-rendered code block. Async-renders into HTML; until the highlighter resolves we render
 *  a plain <pre> so the layout doesn't jump.  The highlighter is shared (see lib/shiki.ts) so
 *  this only pays the worker / WASM cost once. */
export function CodeBlock({ code, language, theme, startLine, compact }: Props) {
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const highlighter = await getHighlighter();
        const lang = shikiLang(language);
        const themeName = shikiTheme(theme);
        const out = highlighter.codeToHtml(code, {
          lang: lang,
          theme: themeName,
        });
        if (!cancelled) setHtml(out);
      } catch (err) {
        // Highlighter failure shouldn't break the row; fall back to plain pre.
        console.warn('shiki render failed', err);
        if (!cancelled) setHtml(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, language, theme]);

  if (html === null) {
    return (
      <pre
        className={
          'overflow-x-auto rounded border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 p-3 font-mono ' +
          (compact ? 'text-xs leading-5' : 'text-sm leading-6')
        }
      >
        <code>{code}</code>
      </pre>
    );
  }

  return (
    <div
      className={
        'code-scroll overflow-x-auto rounded border border-zinc-200 dark:border-zinc-800 p-3 font-mono ' +
        (compact ? 'text-xs leading-5' : 'text-sm leading-6')
      }
      style={
        startLine !== undefined
          ? { ['--start-line' as string]: startLine }
          : undefined
      }
      // Shiki returns trusted HTML built from our own input; safe to set directly.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
